import { useRef } from "react";
import { Box, CanvasIcon, Row, Text, useSize } from "@hightouch/htui";
import { useInView } from "motion/react";
import { useAmpLibraryCanvasQuery } from "src/graphql";
import {
  CanvasAdsProvider,
  useCanvasAds,
} from "src/pages/projects/library/canvas/canvas-ads";
import { NodeBody } from "src/pages/projects/library/canvas/canvas-file-node";

/** Enough to read as the canvas without drawing every email on a busy one. */
const MAX_NODES = 12;
/** Room left around the drawing, as a share of the thumbnail. */
const FIT = 0.86;

/** A canvas drawn small with its files where they sit. It loads only once its card nears the screen. */
export function CanvasThumbnail({
  canvasId,
  isCompact = false,
}: {
  canvasId: string;
  /** Too small for words, so an empty or loading canvas shows only a small icon. */
  isCompact?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isNearViewport = useInView(ref, { margin: "200px" });
  const { data } = useAmpLibraryCanvasQuery(
    { id: canvasId },
    { enabled: isNearViewport },
  );
  const world = useRef<HTMLDivElement>(null);
  const frameSize = useSize(ref);
  const nodes = (data?.ampCanvas.nodes ?? [])
    .flatMap((node) => (node.file ? [{ ...node, file: node.file }] : []))
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0))
    .slice(-MAX_NODES);
  // The drawing mounts only after nodes load. Start measuring then, rather than observing a null ref once.
  const worldSize = useSize(world, { enabled: nodes.length > 0 });
  const ads = useCanvasAds(
    isNearViewport
      ? nodes.flatMap(({ file }) =>
          file.ad
            ? [{ assetId: file.ad.assetId, versionId: file.versionId }]
            : [],
        )
      : [],
  );
  const minX = Math.min(...nodes.map((node) => node.x));
  const minY = Math.min(...nodes.map((node) => node.y));
  const scale =
    frameSize && worldSize?.width && worldSize.height
      ? Math.min(
          1,
          (frameSize.width * FIT) / worldSize.width,
          (frameSize.height * FIT) / worldSize.height,
        )
      : null;
  return (
    <Box ref={ref} position="relative" height="100%" overflow="hidden">
      {isCompact && !nodes.length ? (
        <Row height="100%" align="center" justify="center">
          <Box as={CanvasIcon} boxSize={4} color="text.tertiary" />
        </Row>
      ) : data && !nodes.length ? (
        <Row height="100%" align="center" justify="center">
          <Text size="sm" color="text.secondary">
            Empty canvas
          </Text>
        </Row>
      ) : !data ? (
        <Row height="100%" align="center" justify="center">
          <Box as={CanvasIcon} boxSize={10} color="text.tertiary" />
        </Row>
      ) : (
        <Box
          ref={world}
          {...{ inert: "" }}
          position="absolute"
          left="50%"
          top="50%"
          display="grid"
          opacity={scale ? 1 : 0}
          transform={`translate(-50%, -50%) scale(${scale ?? 1})`}
        >
          <CanvasAdsProvider value={ads}>
            {nodes.map((node) => (
              // Every node shares one grid cell, offset by its position, so the grid grows to the drawing's real extent.
              <Box
                key={node.id}
                gridArea="1 / 1"
                alignSelf="start"
                justifySelf="start"
                width={72}
                ml={`${node.x - minX}px`}
                mt={`${node.y - minY}px`}
                overflow="hidden"
                borderRadius={node.file.type === "ad" ? "sm" : "2xl"}
                boxShadow="sm"
              >
                <NodeBody file={node.file} sender={null} />
              </Box>
            ))}
          </CanvasAdsProvider>
        </Box>
      )}
    </Box>
  );
}
